<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import i18n from "@/i18n";
import { SHARED } from "@/constants";
import { mapState } from "vuex";
import { SALES_DOC_MODULE } from "@/state/modules/salesDoc";
import { DOC_TYPES, downloadSalesDocPdf } from "@/helpers/services/salesDoc.service";
import { generateSigningLink } from "@/helpers/services/documentSignature.service";
import { LOOKUP_MODULE } from "@/state/modules/lookup";
import { salesDocMethods, lookupMethods, approvalMethods } from "@/state/helpers";

export default {
  page: { title: i18n.t("label.listingX", [i18n.t("module.salesDoc")]) },
  components: { Layout, PageHeader },
  data() {
    return {
      title: i18n.t("label.listingX", [i18n.t("module.salesDoc")]),
      activeDocType: "quotation",
      currentPage: 1,
      filter: "",
      perPage: SHARED.DEFAULT_PAGE_SIZE,
      searchCriteria: {},
      // Mark Lost reason modal
      lostTarget: null,
      lostReason: "",
      processing: false,
      // Share for signature modal
      shareTarget: null,
      shareForm: { signer_type: "customer", expires_hours: 72 },
      shareResult: null, // { signing_url, expires_at }
      shareGenerating: false,
      shareCopied: false,
    };
  },
  computed: {
    ...mapState(SALES_DOC_MODULE, ["status"]),
    ...mapState(LOOKUP_MODULE, ["salesDocTypeDdl"]),
    tableRows() { return this.$store.state.salesDoc.list.data; },
    pagination() { return this.$store.state.salesDoc.list.pagination; },
    isQuotationTab() { return this.activeDocType === "quotation"; },
    isSalesOrderTab() { return this.activeDocType === "salesorder"; },
    isSalesInvoiceTab() { return this.activeDocType === "salesinvoice"; },
    isDeliveryOrderTab() { return this.activeDocType === "deliveryorder"; },
    // Auto-generated docs (DO / DR) are view-only here — created from Deliveries.
    isReadOnlyDocTab() { return ["deliveryorder", "deliveryreturn"].includes(this.activeDocType); },
    docTypes() {
      // Server-filtered list of enabled doc types (driven by SALES_DOC_TYPES env).
      // Show nothing until the enabled list loads — never fall back to the full
      // list, or disabled types (debitnote/creditnote/deliveryreturn) would show
      // as tabs and hit "Document type not enabled" when fetched.
      const enabled = (this.salesDocTypeDdl || []).map((t) => t.value);
      if (!enabled.length) return [];
      return DOC_TYPES.filter((t) => enabled.includes(t));
    },
    fields() {
      const base = [
        { key: "docNo", label: i18n.t("label.docNo") },
        { key: "docDate", label: i18n.t("label.docDate") },
        { key: "debtorCode", label: i18n.t("label.debtorCode") },
        { key: "debtorName", label: i18n.t("label.debtorName") },
        { key: "agent", label: i18n.t("label.agent") },
        { key: "total", label: i18n.t("label.total") },
      ];
      if (this.isQuotationTab) {
        base.push({ key: "priceBinding", label: i18n.t("quotation.bindingColumn") });
      }
      // One status column for every kind: held / rejected / edit pending /
      // posted, and on quotations the won-lost state on top of that.
      base.push({ key: "status", label: i18n.t("label.status") });
      base.push({ key: "action", label: i18n.t("label.action") });
      return base;
    },
    bindingVariant() {
      return { once: "secondary", always: "primary", min_qty: "info" };
    },
    statusVariant() {
      return { pending: "warning", won: "success", lost: "danger" };
    },
  },
  async created() {
    this.searchCriteria = { pageNumber: 1, pageSize: SHARED.DEFAULT_PAGE_SIZE, searchArray: [] };
    // Restore the tab the user was on before navigating away (e.g. into the
    // create/edit form) so coming back doesn't snap them to "quotation".
    const remembered = (() => {
      try { return localStorage.getItem("salesDoc.activeTab") || ""; }
      catch (_) { return ""; }
    })();
    if (remembered) this.activeDocType = remembered;

    await this.lookupSalesDocType();
    // Drop to the first enabled tab only if the current selection is disabled.
    const enabled = (this.salesDocTypeDdl || []).map((t) => t.value);
    if (enabled.length && !enabled.includes(this.activeDocType)) {
      this.activeDocType = enabled[0];
    }
    this.fetch();
  },
  methods: {
    ...salesDocMethods,
    ...approvalMethods,
    ...lookupMethods,
    fetch() {
      this.getAllSalesDocs({ docType: this.activeDocType, filter: this.searchCriteria });
    },
    onTabChange(t) {
      this.activeDocType = t;
      try { localStorage.setItem("salesDoc.activeTab", t); } catch (_) { /* ignore */ }
      this.searchCriteria.pageNumber = 1;
      this.searchCriteria.searchArray = [];
      this.filter = "";
      this.fetch();
    },
    pageNavigation(p) { this.searchCriteria.pageNumber = p; this.fetch(); },
    searchListing() {
      this.searchCriteria.pageNumber = 1;
      this.searchCriteria.searchArray = [this.filter];
      this.fetch();
    },
    goCreate() {
      this.$router.push({ name: "Sales-doc-create", params: { docType: this.activeDocType } });
    },
    goEdit(row) {
      if (row.approvalUuid) return this.goPending(row.approvalUuid);
      this.$router.push({ name: "Sales-doc-update", params: { docType: this.activeDocType, docNo: row.docNo } });
    },
    /** A held document opens from its approval — the ledger has nothing to show. */
    goPending(approvalUuid) {
      this.$router.push({ name: "Sales-doc-pending", params: { docType: this.activeDocType, approvalUuid } });
    },
    approvalActionLabel(row) {
      return {
        convert: this.$t("label.convertToInvoice"),
        win: this.$t("label.convertToInvoice"),
        delivery: this.$t("label.deliveryDocument"),
      }[row.approvalAction] || this.$t("label.newDocument");
    },
    async remove(row) {
      if (!confirm(`Delete ${row.docNo}?`)) return;
      await this.deleteSalesDoc({ docType: this.activeDocType, docNo: row.docNo, filter: this.searchCriteria });
    },
    /**
     * A held row has no document to delete — the request IS the document so
     * far — so the ledger's delete cannot reach it. Throw the request away
     * instead. Same for a rejected one, which otherwise sat on the list with
     * no way off it.
     */
    async removeHeld(row) {
      const what = row.approvalStatus === "rejected"
        ? this.$t("label.rejected")
        : this.$t("label.pendingApproval");
      if (!confirm(`${this.$t("label.delete")} ${what}: ${row.debtorName || row.debtorCode || ""}?`)) return;
      await this.deleteApproval({ id: row.approvalUuid });
      this.fetch();
    },
    isQuotationLocked(row) {
      return row && (row.quotationStatus === "won" || row.quotationStatus === "lost");
    },
    isConvertedSo(row) {
      return this.isSalesOrderTab && row && row.isConverted === true;
    },
    bindingLabel(b) {
      if (b === "always") return i18n.t("quotation.bindingAlways");
      if (b === "min_qty") return i18n.t("quotation.bindingMinQty");
      return i18n.t("quotation.bindingOnce");
    },
    /** Expiry only applies to a carried-forward binding. */
    bindingUntil(row) {
      if (!row || !row.priceBindingUntil || row.priceBinding === "once") return "";
      return String(row.priceBindingUntil).substr(0, 10);
    },
    bindingTooltip(row) {
      const until = this.bindingUntil(row);
      const label = this.bindingLabel(row.priceBinding);
      return until ? `${label} — ${i18n.t("quotation.priceValidUntilX", [until])}` : label;
    },
    async markWon(row) {
      // A ledger with neither Sales Orders nor Cash Sales (Xero, QuickBooks)
      // can only take the deal as an Invoice, so there is nothing to choose.
      if (!this.$supports("sales_order") && !this.$supports("cash_sale")) {
        const ok = await this.$swal({
          title: i18n.t("quotation.wonChooseTitle", [row.docNo]),
          text: i18n.t("quotation.wonInvoiceText", [row.docNo]),
          icon: "question",
          showCancelButton: true,
          confirmButtonText: i18n.t("quotation.wonInvoice"),
          cancelButtonText: i18n.t("label.cancel"),
          confirmButtonColor: "#7C4DFF",
        });
        if (!ok.isConfirmed) return;
        this.processing = true;
        try {
          await this.markQuotationWon({ docNo: row.docNo, data: { target: "salesinvoice" } });
          this.fetch();
        } finally {
          this.processing = false;
        }
        return;
      }

      // Let the user pick the target doc by how the customer pays:
      //   Cash Sale = paid now, Sales Order = credit/term (billed later).
      // Offer only the kinds this ledger has.
      const result = await this.$swal({
        title: i18n.t("quotation.wonChooseTitle", [row.docNo]),
        text: i18n.t("quotation.wonChooseText"),
        icon: "question",
        showDenyButton: this.$supports("cash_sale"),
        showConfirmButton: this.$supports("sales_order"),
        showCancelButton: true,
        confirmButtonText: i18n.t("quotation.wonSalesOrder"),
        denyButtonText: i18n.t("quotation.wonCashSale"),
        cancelButtonText: i18n.t("label.cancel"),
        confirmButtonColor: "#7C4DFF",
        denyButtonColor: "#1cbb8c",
      });
      let target = null;
      if (result.isConfirmed) target = "salesorder";
      else if (result.isDenied) target = "cashsale";
      else return; // cancelled

      this.processing = true;
      try {
        await this.markQuotationWon({ docNo: row.docNo, data: { target } });
        this.fetch();
      } finally {
        this.processing = false;
      }
    },
    async convertToInvoice(row) {
      const result = await this.$swal({
        title: i18n.t("salesDoc.convertToInvoiceTitle", [row.docNo]),
        text: i18n.t("salesDoc.convertToInvoiceText"),
        icon: "question",
        showCancelButton: true,
        confirmButtonText: i18n.t("salesDoc.convertToInvoice"),
        cancelButtonText: i18n.t("label.cancel"),
        confirmButtonColor: "#7C4DFF",
      });
      if (!result.isConfirmed) return;

      this.processing = true;
      try {
        const r = await this.convertSoToSi({ docNo: row.docNo });
        // A 202 means no invoice was raised — the conversion is waiting for
        // approval, and the order is deliberately still unconverted.
        if (r?.pending_approval) {
          this.$router.push({ name: "Approval-listing" });
          return;
        }
        if (r) this.fetch();
      } finally {
        this.processing = false;
      }
    },
    openLostModal(row) {
      this.lostTarget = row;
      this.lostReason = "";
      this.$bvModal.show("markLostModal");
    },
    openShareModal(row) {
      this.shareTarget = row;
      this.shareForm = { signer_type: "customer", expires_hours: 72 };
      this.shareResult = null;
      this.shareCopied = false;
      this.$bvModal.show("shareSignatureModal");
    },
    async generateShareLink() {
      if (!this.shareTarget) return;
      this.shareGenerating = true;
      try {
        const r = await generateSigningLink({
          doc_type: this.activeDocType,
          doc_no: this.shareTarget.docNo,
          ...this.shareForm,
        });
        // apiHelper unwraps response.data; r === { code, success, data: { signing_url, ... } }
        this.shareResult = r?.data || null;
        // Auto-copy the freshly generated URL.
        if (this.shareResult?.signing_url && navigator.clipboard) {
          try {
            await navigator.clipboard.writeText(this.shareResult.signing_url);
            this.shareCopied = true;
            setTimeout(() => { this.shareCopied = false; }, 2500);
          } catch (_) { /* clipboard denied; user can click Copy */ }
        }
      } catch (_) { /* alerted upstream */ }
      finally { this.shareGenerating = false; }
    },
    copyShareUrl() {
      if (!this.shareResult?.signing_url) return;
      navigator.clipboard.writeText(this.shareResult.signing_url).then(() => {
        this.shareCopied = true;
        setTimeout(() => { this.shareCopied = false; }, 2000);
      });
    },
    async exportPdf(row) {
      try {
        // apiHelper interceptor unwraps response.data, so `r` is the Blob itself.
        const r = await downloadSalesDocPdf(this.activeDocType, row.docNo);
        const blob = r instanceof Blob ? r : new Blob([r], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${this.activeDocType}_${row.docNo}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (_) { /* error already alerted by interceptor */ }
    },
    async confirmLost() {
      if (!this.lostTarget) return;
      this.processing = true;
      try {
        await this.markQuotationLost({ docNo: this.lostTarget.docNo, data: { reason: this.lostReason } });
        this.$bvModal.hide("markLostModal");
        this.lostTarget = null;
        this.lostReason = "";
        this.fetch();
      } finally {
        this.processing = false;
      }
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" />
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <b-nav tabs class="mb-3 flex-wrap">
              <b-nav-item v-for="t in docTypes" :key="t" :active="activeDocType === t" @click="onTabChange(t)">
                {{ $t('module.' + t) }}
              </b-nav-item>
            </b-nav>

            <div class="row mt-2 mb-3">
              <div class="col-sm-12 col-md-6">
                <label class="d-inline-flex align-items-center">
                  {{ $t('label.search') }}
                  <b-form-input v-model="filter" type="search" class="form-control form-control-sm ml-2" @change="searchListing"></b-form-input>
                </label>
              </div>
              <div class="col-sm-12 col-md-6 text-md-right">
                <b-button v-if="!isReadOnlyDocTab && !isSalesInvoiceTab && $can('Create Sales Document')" variant="primary" size="sm" @click="goCreate">
                  <i class="mdi mdi-plus mr-1"></i>{{ $t('label.createX', [$t('module.' + activeDocType)]) }}
                </b-button>
              </div>
            </div>

            <b-overlay :show="processing" rounded="sm" :opacity="0.5" spinner-variant="primary">
            <div class="table-responsive mb-0">
              <b-table :items="tableRows" :fields="fields" responsive="sm" :per-page="perPage">
                <template #cell(priceBinding)="data">
                  <b-badge
                    :variant="bindingVariant[data.item.priceBinding] || 'secondary'"
                    v-b-tooltip.hover
                    :title="bindingTooltip(data.item)"
                  >
                    {{ bindingLabel(data.item.priceBinding) }}
                  </b-badge>
                  <small v-if="bindingUntil(data.item)" class="text-muted d-block">
                    {{ $t('quotation.priceValidUntilX', [bindingUntil(data.item)]) }}
                  </small>
                </template>
                <template #cell(status)="data">
                  <template v-if="data.item.approvalUuid">
                    <b-badge :variant="data.item.approvalStatus === 'rejected' ? 'danger' : 'warning'">
                      {{ data.item.approvalStatus === 'rejected' ? $t('label.rejected') : $t('label.pendingApproval') }}
                    </b-badge>
                    <small class="text-muted d-block">{{ approvalActionLabel(data.item) }}<span v-if="data.item.sourceDocNo"> · {{ data.item.sourceDocNo }}</span></small>
                  </template>
                  <template v-else>
                    <b-badge v-if="isQuotationTab" :variant="statusVariant[data.item.quotationStatus] || 'secondary'">
                      {{ $t('quotation.status.' + (data.item.quotationStatus || 'pending')) }}
                    </b-badge>
                    <b-badge v-else variant="success">{{ $t('label.posted') }}</b-badge>
                    <a v-if="data.item.pendingEditUuid" href="javascript:void(0);" class="d-block small" @click="goPending(data.item.pendingEditUuid)">
                      <b-badge variant="warning">{{ $t('label.pendingEdit') }}</b-badge>
                    </a>
                  </template>
                </template>
                <template #cell(action)="data">
                  <!-- A held document has no PDF, share link or conversion yet —
                       it is not in the ledger. Open it, and decide on it there. -->
                  <template v-if="data.item.approvalUuid">
                    <a href="javascript:void(0);" @click="goPending(data.item.approvalUuid)" class="text-info mr-2" v-b-tooltip.hover.left :title="$t('label.view')">
                      <i class="mdi mdi-pencil font-size-18"></i>
                    </a>
                    <a v-if="$can('Delete Sales Document')" href="javascript:void(0);" @click="removeHeld(data.item)" class="text-danger mr-2" v-b-tooltip.hover.left :title="$t('label.delete')">
                      <i class="mdi mdi-delete font-size-18"></i>
                    </a>
                  </template>
                  <!-- Delivery orders are auto-created from SO/SI and must not
                       be edited / deleted / re-shared / re-exported from the
                       listing. Only "View details" (read-only) is allowed. -->
                  <template v-else-if="isDeliveryOrderTab">
                    <a href="javascript:void(0);" @click="goEdit(data.item)" class="text-info mr-2" v-b-tooltip.hover.left :title="$t('label.view')">
                      <i class="mdi mdi-eye-outline font-size-18"></i>
                    </a>
                  </template>
                  <!-- A converted sales order is view/delete only. -->
                  <template v-else-if="isConvertedSo(data.item)">
                    <a href="javascript:void(0);" @click="goEdit(data.item)" class="text-info mr-2" v-b-tooltip.hover.left :title="$t('label.view')">
                      <i class="mdi mdi-eye-outline font-size-18"></i>
                    </a>
                    <a v-if="$can('Delete Sales Document')" href="javascript:void(0);" @click="remove(data.item)" class="text-danger mr-2" v-b-tooltip.hover.left title="Delete">
                      <i class="mdi mdi-delete font-size-18"></i>
                    </a>
                    <b-badge variant="success" class="ml-1">{{ $t('salesDoc.converted') }}</b-badge>
                  </template>
                  <template v-else-if="!isQuotationLocked(data.item)">
                    <a v-if="!isSalesInvoiceTab" href="javascript:void(0);" @click="goEdit(data.item)" class="text-info mr-2" v-b-tooltip.hover.left :title="$t('label.view')">
                      <i class="mdi mdi-pencil font-size-18"></i>
                    </a>
                    <a v-if="$can('Delete Sales Document')" href="javascript:void(0);" @click="remove(data.item)" class="text-danger mr-2" v-b-tooltip.hover.left title="Delete">
                      <i class="mdi mdi-delete font-size-18"></i>
                    </a>
                    <a href="javascript:void(0);" @click="exportPdf(data.item)" class="text-primary mr-2" v-b-tooltip.hover.left :title="$t('label.exportPdf')">
                      <i class="mdi mdi-file-pdf-box font-size-18"></i>
                    </a>
                    <a href="javascript:void(0);" @click="openShareModal(data.item)" class="text-success mr-2" v-b-tooltip.hover.left :title="$t('label.shareForSign')">
                      <i class="mdi mdi-share-variant font-size-18"></i>
                    </a>
                    <a v-if="isSalesOrderTab && $can('Create Sales Document')" href="javascript:void(0);" @click="convertToInvoice(data.item)" class="text-success mr-2" v-b-tooltip.hover.left :title="$t('salesDoc.convertToInvoice')">
                      <i class="mdi mdi-receipt font-size-18"></i>
                    </a>
                    <template v-if="isQuotationTab && $can('Update Sales Document')">
                      <a href="javascript:void(0);" @click="markWon(data.item)" class="text-success mr-2" v-b-tooltip.hover.left :title="$t('quotation.markWon')">
                        <i class="mdi mdi-check-circle font-size-18"></i>
                      </a>
                      <a href="javascript:void(0);" @click="openLostModal(data.item)" class="text-danger" v-b-tooltip.hover.left :title="$t('quotation.markLost')">
                        <i class="mdi mdi-close-circle font-size-18"></i>
                      </a>
                    </template>
                  </template>
                  <template v-else>
                    <a href="javascript:void(0);" @click="goEdit(data.item)" class="text-info mr-2" v-b-tooltip.hover.left :title="$t('label.view')">
                      <i class="mdi mdi-eye-outline font-size-18"></i>
                    </a>
                    <a href="javascript:void(0);" @click="exportPdf(data.item)" class="text-primary mr-2" v-b-tooltip.hover.left :title="$t('label.exportPdf')">
                      <i class="mdi mdi-file-pdf-box font-size-18"></i>
                    </a>
                    <a href="javascript:void(0);" @click="openShareModal(data.item)" class="text-success mr-2" v-b-tooltip.hover.left :title="$t('label.shareForSign')">
                      <i class="mdi mdi-share-variant font-size-18"></i>
                    </a>
                    <span class="text-muted small">
                      <i class="mdi mdi-lock-outline mr-1"></i>{{ $t('quotation.locked') }}
                    </span>
                  </template>
                </template>
              </b-table>
            </div>
            </b-overlay>

            <b-modal id="shareSignatureModal" :title="$t('share.title')" hide-footer size="md">
              <div v-if="!shareResult">
                <p class="text-muted small">{{ $t('share.hint') }}</p>
                <b-form-group :label="$t('share.signerType')">
                  <b-form-select v-model="shareForm.signer_type" :options="[{value:'customer', text: $t('share.signerCustomer')}, {value:'received_by', text: $t('share.signerReceivedBy')}, {value:'issued_by', text: $t('share.signerIssuedBy')}]" />
                </b-form-group>
                <b-form-group :label="$t('share.expiresHours')">
                  <b-form-input v-model.number="shareForm.expires_hours" type="number" min="1" max="720" />
                </b-form-group>
                <div class="text-right">
                  <b-button variant="light" class="mr-2" @click="$bvModal.hide('shareSignatureModal')">{{ $t('label.cancel') }}</b-button>
                  <b-button variant="primary" :disabled="shareGenerating" @click="generateShareLink">
                    <b-spinner v-if="shareGenerating" small class="mr-1" />
                    {{ $t('share.generate') }}
                  </b-button>
                </div>
              </div>
              <div v-else>
                <div class="alert alert-success small mb-3">
                  <i class="mdi mdi-check-circle mr-1"></i>
                  {{ shareCopied ? $t('share.generatedCopied') : $t('share.generated') }}
                </div>
                <b-form-group :label="$t('share.copyLink')">
                  <b-input-group>
                    <b-form-input :value="shareResult.signing_url" readonly />
                    <b-input-group-append>
                      <b-button :variant="shareCopied ? 'success' : 'primary'" @click="copyShareUrl">
                        <i :class="shareCopied ? 'mdi mdi-check' : 'mdi mdi-content-copy'"></i>
                        {{ shareCopied ? $t('share.copied') : $t('share.copy') }}
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                  <small v-if="shareResult.expires_at" class="text-muted">{{ $t('share.expiresAt', [new Date(shareResult.expires_at).toLocaleString()]) }}</small>
                </b-form-group>
                <div class="text-right">
                  <b-button variant="light" @click="$bvModal.hide('shareSignatureModal')">{{ $t('label.close') }}</b-button>
                </div>
              </div>
            </b-modal>

            <b-modal id="markLostModal" :title="$t('quotation.markLostTitle')" hide-footer>
              <p class="text-muted small">{{ $t('quotation.markLostHint') }}</p>
              <b-form-group :label="$t('quotation.lostReason')">
                <b-form-textarea v-model="lostReason" rows="3" :placeholder="$t('quotation.lostReasonHint')" />
              </b-form-group>
              <div class="text-right">
                <b-button variant="light" class="mr-2" @click="$bvModal.hide('markLostModal')">{{ $t('label.cancel') }}</b-button>
                <b-button variant="danger" :disabled="processing" @click="confirmLost">
                  <b-spinner v-if="processing" small class="mr-1" />
                  {{ $t('quotation.confirmLostAction') }}
                </b-button>
              </div>
            </b-modal>

            <div class="row">
              <div class="col">
                <div class="dataTables_paginate paging_simple_numbers float-right">
                  <ul class="pagination pagination-rounded mb-0">
                    <b-pagination v-model="currentPage" :total-rows="pagination.total" :per-page="perPage" @change="pageNavigation"></b-pagination>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
